import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase/firebase";
import {
  getAssignedClasses,
  getCurrentUser,
  isAdminUser,
} from "../utils/auth";

function toDate(value) {
  if (!value) return null;

  if (value instanceof Date) return value;

  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "object" && typeof value.seconds === "number") {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function normalizeClass(value) {
  return String(value || "").trim().toLowerCase();
}

function sameClass(a, b) {
  return normalizeClass(a) === normalizeClass(b);
}

function normalizeAssignedClasses(user) {
  if (!user) return [];

  let raw = [];

  if (Array.isArray(user.assignedClasses)) {
    raw = user.assignedClasses;
  } else if (Array.isArray(getAssignedClasses(user))) {
    raw = getAssignedClasses(user);
  } else if (user.primaryClass) {
    raw = [user.primaryClass];
  }

  return Array.from(
    new Set(
      raw.map((entry) => String(entry || "").trim()).filter(Boolean)
    )
  );
}

function getScopeMeta() {
  const currentUser = getCurrentUser();
  const adminMode = isAdminUser(currentUser);
  const assignedClasses = adminMode ? [] : normalizeAssignedClasses(currentUser);

  return {
    currentUser,
    isAdmin: adminMode,
    assignedClasses,
    assignedClassKeys: assignedClasses.map(normalizeClass),
  };
}

function canAccessUserRecord(user) {
  const { isAdmin, assignedClassKeys } = getScopeMeta();

  if (isAdmin) return true;
  if (!user) return false;

  const className =
    user.className ||
    user.schoolClass ||
    user.class ||
    user.primaryClass ||
    null;

  if (!className) return false;

  return assignedClassKeys.includes(normalizeClass(className));
}

function normalizeUser(entry) {
  const isDocSnapshot = typeof entry?.data === "function";
  const data = isDocSnapshot ? entry.data() : entry || {};
  const id = entry?.id || data.uid || "";

  return {
    id,
    uid: id,
    displayName:
      data.displayName ||
      data.name ||
      data.username ||
      data.email ||
      "Unbekannter User",
    name:
      data.name ||
      data.displayName ||
      data.username ||
      data.email ||
      "Unbekannter User",
    email: data.email || "",
    className:
      data.className ||
      data.schoolClass ||
      data.class ||
      data.primaryClass ||
      "",
    schoolName: data.schoolName || data.school || "",
    streak: Number(data.streak || 0),
    createdAt: data.createdAt || data.created_at || null,
    lastActiveAt:
      data.lastActiveAt ||
      data.updatedAt ||
      data.lastLoginAt ||
      data.lastSeenAt ||
      null,
    ...data,
  };
}

async function fetchScopedUsers() {
  const { isAdmin, assignedClasses } = getScopeMeta();

  if (isAdmin) {
    const usersSnap = await getDocs(collection(db, "users")).catch(() => null);
    return usersSnap?.docs?.map((entry) => normalizeUser(entry)) || [];
  }

  if (!assignedClasses.length) return [];

  const snapshots = await Promise.all(
    assignedClasses.map((className) =>
      getDocs(
        query(collection(db, "users"), where("className", "==", String(className).trim()))
      ).catch((error) => {
        console.error("Scoped users query failed for class:", className, error);
        return null;
      })
    )
  );

  const merged = new Map();

  snapshots.forEach((snap) => {
    snap?.docs?.forEach((docSnap) => {
      const user = normalizeUser(docSnap);
      if (user?.id) merged.set(user.id, user);
    });
  });

  return Array.from(merged.values());
}

async function fetchRootCollectionByUserId(collectionName, userId = null) {
  const snap = await getDocs(collection(db, collectionName)).catch(() => null);

  const rows =
    snap?.docs?.map((entry) => ({
      id: entry.id,
      ...entry.data(),
    })) || [];

  if (userId) {
    return rows.filter((entry) => entry.userId === userId);
  }

  return rows;
}

async function fetchScopedUserSubcollection(userId, subcollectionName) {
  const userDoc = await getDoc(doc(db, "users", userId)).catch(() => null);

  if (!userDoc?.exists()) {
    const fallback = await fetchRootCollectionByUserId(subcollectionName, userId);
    return fallback.map((entry) => ({
      ...entry,
      userId: entry.userId || userId,
    }));
  }

  const user = normalizeUser({ id: userDoc.id, ...userDoc.data() });

  if (!canAccessUserRecord(user)) return [];

  const nestedRef = collection(db, "users", userId, subcollectionName);

  const nestedSnap = await getDocs(
    query(nestedRef, orderBy("createdAt", "desc"))
  ).catch(async () => {
    return getDocs(nestedRef).catch(() => null);
  });

  if (nestedSnap) {
    return nestedSnap.docs.map((entry) => ({
      id: entry.id,
      userId,
      ...entry.data(),
    }));
  }

  const fallback = await fetchRootCollectionByUserId(subcollectionName, userId);

  return fallback.map((entry) => ({
    ...entry,
    userId: entry.userId || userId,
  }));
}

async function fetchScopedCollections(options = {}) {
  const users = await fetchScopedUsers();
  const userIds = users.map((u) => u.id).filter(Boolean);

  const stressLogs = [];
  const tasks = [];
  const focusSessions = [];
  const journal = [];

  await Promise.all(
    userIds.map(async (userId) => {
      const [userStress, userTasks, userFocus, userJournal] = await Promise.all([
        fetchScopedUserSubcollection(userId, "stressLogs"),
        fetchScopedUserSubcollection(userId, "tasks"),
        fetchScopedUserSubcollection(userId, "focusSessions"),
        options.includeJournal
          ? fetchScopedUserSubcollection(userId, "journal")
          : Promise.resolve([]),
      ]);

      stressLogs.push(...userStress);
      tasks.push(...userTasks);
      focusSessions.push(...userFocus);
      journal.push(...userJournal);
    })
  );

  return {
    stressLogs,
    tasks,
    focusSessions,
    journal,
  };
}

export function formatRelativeActivity(value) {
  const date = toDate(value);
  if (!date) return "—";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays <= 0) return "Heute";
  if (diffDays === 1) return "Gestern";
  if (diffDays === 2) return "Vor 2 Tagen";
  if (diffDays < 7) return `Vor ${diffDays} Tagen`;

  return date.toLocaleDateString("de-AT");
}

function formatSince(value) {
  const date = toDate(value);
  if (!date) return "—";

  return date.toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getStressLevelLabel(level) {
  const numeric = Number(level || 0);

  if (numeric >= 4) return "Belastet";
  if (numeric === 3) return "Ruhig";
  return "Motiviert";
}

export function getStressTone(level) {
  const numeric = Number(level || 0);

  if (numeric >= 4) {
    return {
      key: "belastet",
      label: "Belastet",
      dotClass: "is-red",
      pillClass: "is-warn",
    };
  }

  if (numeric === 3) {
    return {
      key: "ruhig",
      label: "Ruhig",
      dotClass: "is-orange",
      pillClass: "is-soft",
    };
  }

  return {
    key: "motiviert",
    label: "Motiviert",
    dotClass: "is-green",
    pillClass: "is-ok",
  };
}

async function fetchTeacherAssignments() {
  const currentUser = getCurrentUser();

  if (!currentUser) return [];

  if (!isAdminUser(currentUser)) {
    const sessionSnap = await getDoc(doc(db, "adminSessions", currentUser.uid)).catch(
      () => null
    );

    return [
      {
        uid: currentUser.uid,
        name: currentUser.name || currentUser.email || "Lehrkraft",
        email: currentUser.email || "",
        role: currentUser.role || "teacher",
        assignedClasses: normalizeAssignedClasses(currentUser),
        sessionActive: sessionSnap?.exists() ? sessionSnap.data().sessionActive === true : false,
        sessionStartedAt: sessionSnap?.exists() ? sessionSnap.data().sessionStartedAt || null : null,
        lastLoginAt: sessionSnap?.exists() ? sessionSnap.data().lastLoginAt || null : null,
      },
    ];
  }

  const [adminsSnap, sessionsSnap] = await Promise.all([
    getDocs(query(collection(db, "admins"), where("role", "in", ["teacher", "viewer"]))),
    getDocs(collection(db, "adminSessions")),
  ]);

  const sessionsByUid = new Map(
    sessionsSnap.docs.map((entry) => [entry.id, entry.data()])
  );

  return adminsSnap.docs
    .map((entry) => {
      const data = entry.data();
      const session = sessionsByUid.get(entry.id) || {};


      return {
        uid: entry.id,
        name: data.name || data.email || "Lehrkraft",
        email: data.email || "",
        role: data.role || "teacher",
        active: data.active === true,
        assignedClasses: Array.from(
  new Set(
    (
      Array.isArray(data.assignedClasses)
        ? data.assignedClasses
        : data.primaryClass
        ? [data.primaryClass]
        : []
    )
      .map((cls) => String(cls || "").trim())
      .filter(Boolean)
  )
),
        sessionActive: session.sessionActive === true,
        sessionStartedAt: session.sessionStartedAt || null,
        lastLoginAt: session.lastLoginAt || null,
      };
    })
    .filter((entry) => entry.active !== false);
}

function buildTeacherMetaForClass(className, teachers) {
  const matches = teachers.filter((teacher) =>
    Array.isArray(teacher.assignedClasses)
      ? teacher.assignedClasses.some((assigned) => sameClass(assigned, className))
      : false
  );

  if (!matches.length) {
    return {
      teacherAssigned: false,
      teacherNames: "—",
      teacherInfo: "Kein Lehrkraft-Zugang verknüpft",
      teacherSessionActive: false,
    };
  }

  const preferred = matches.find((entry) => entry.sessionActive) || matches[0];
  const names = matches.map((entry) => entry.name).join(", ");

  let teacherInfo = "Lehrkraft verknüpft";

  if (preferred.sessionActive) {
    teacherInfo = `eingeloggt seit ${formatSince(
      preferred.sessionStartedAt || preferred.lastLoginAt
    )}`;
  } else if (preferred.lastLoginAt) {
    teacherInfo = `zuletzt eingeloggt ${formatRelativeActivity(preferred.lastLoginAt)}`;
  } else {
    teacherInfo = "freigegeben";
  }

  return {
    teacherAssigned: true,
    teacherNames: names,
    teacherInfo,
    teacherSessionActive: preferred.sessionActive === true,
    teacherSessionSince: preferred.sessionStartedAt || preferred.lastLoginAt || null,
  };
}

export async function fetchUsers() {
  const users = await fetchScopedUsers();

  return users.map((data) => ({
    ...data,
    createdAtRaw: data.createdAt || null,
    lastActiveAtRaw: data.lastActiveAt || null,
    createdAtFormatted: toDate(data.createdAt)
      ? toDate(data.createdAt).toLocaleDateString("de-AT")
      : "—",
    lastActiveLabel: formatRelativeActivity(data.lastActiveAt),
  }));
}

export async function fetchClasses() {
  const { isAdmin, assignedClassKeys } = getScopeMeta();
  const users = await fetchUsers();

  const classesSnap = await getDocs(collection(db, "classes")).catch(() => null);
  const allClassDocs =
    classesSnap?.docs?.map((entry) => ({ id: entry.id, ...entry.data() })) || [];

  let classDocs = allClassDocs;

  if (!isAdmin) {
    classDocs = allClassDocs.filter((item) =>
      assignedClassKeys.includes(
        normalizeClass(item.name || item.className || item.id)
      )
    );
  }

  if (classDocs.length > 0) {
    return classDocs
      .map((item) => ({
        id: item.id,
        name: item.name || item.className || item.id,
        school: item.school || item.schoolName || "—",
        year: item.year || null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }

  const derived = Array.from(
    new Map(
      users
        .filter((u) => u.className)
        .map((u) => [
          normalizeClass(u.className),
          {
            id: String(u.className).toLowerCase().replace(/\s+/g, "-"),
            name: u.className,
            school: u.schoolName || "—",
            year: null,
          },
        ])
    ).values()
  );

  if (!isAdmin) {
    return derived
      .filter((entry) => assignedClassKeys.includes(normalizeClass(entry.name)))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }

  return derived.sort((a, b) => a.name.localeCompare(b.name, "de"));
}

export async function fetchUserSubcollection(userId, subcollectionName) {
  return fetchScopedUserSubcollection(userId, subcollectionName);
}

export async function fetchUserDetails(userId) {
  const [users, scopedCollections] = await Promise.all([
    fetchUsers(),
    fetchScopedCollections({ includeJournal: true }),
  ]);

  const user = users.find((entry) => entry.id === userId) || null;

  if (!user || !canAccessUserRecord(user)) {
    return null;
  }

  const stressLogs = scopedCollections.stressLogs.filter((entry) => entry.userId === userId);
  const tasks = scopedCollections.tasks.filter((entry) => entry.userId === userId);
  const focusSessions = scopedCollections.focusSessions.filter((entry) => entry.userId === userId);
  const journal = scopedCollections.journal.filter((entry) => entry.userId === userId);

  const averageStress =
    stressLogs.length > 0
      ? Number(
          (
            stressLogs.reduce(
              (sum, entry) => sum + Number(entry.stressLevel || 0),
              0
            ) / stressLogs.length
          ).toFixed(1)
        )
      : 0;

  const recentLogs = [
    ...stressLogs.map((entry) => ({
      t: entry.date || "Stress",
      text: `Stress Check-In: ${Number(entry.stressLevel || 0)}/5`,
      sortDate: entry.updatedAt || entry.createdAt || entry.date || null,
    })),
    ...focusSessions.map((entry) => ({
      t: entry.date || "Fokus",
      text: `Fokus-Session: ${Number(entry.minutes || 0)} Minuten`,
      sortDate: entry.finishedAt || entry.updatedAt || entry.date || null,
    })),
    ...tasks.map((entry) => ({
      t: entry.date || "Aufgabe",
      text: `Task: ${entry.title || "Ohne Titel"}${entry.done ? " · erledigt" : " · offen"}`,
      sortDate: entry.updatedAt || entry.createdAt || entry.date || null,
    })),
    ...journal.map((entry) => ({
      t: entry.date || "Journal",
      text: entry.text || "Journal-Eintrag",
      sortDate: entry.updatedAt || entry.createdAt || entry.date || null,
    })),
  ]
    .sort((a, b) => {
      const da = toDate(a.sortDate)?.getTime() || 0;
      const db = toDate(b.sortDate)?.getTime() || 0;
      return db - da;
    })
    .slice(0, 8)
    .map((item) => ({
      t: item.t,
      text: item.text,
    }));

  return {
    user,
    stressLogs,
    tasks,
    focusSessions,
    journal,
    averageStress,
    openTasks: tasks.filter((entry) => entry.done !== true).length,
    completedTasks: tasks.filter((entry) => entry.done === true).length,
    totalFocusMinutes: focusSessions.reduce(
      (sum, entry) => sum + Number(entry.minutes || 0),
      0
    ),
    recentLogs,
  };
}

export async function fetchUsersClassesOverview() {
  const [users, classes, teachers] = await Promise.all([
    fetchUsers(),
    fetchClasses(),
    fetchTeacherAssignments(),
  ]);

  const userDetailsList = await Promise.all(
    users.map(async (user) => {
      const [stressLogs, tasks, focusSessions] = await Promise.all([
        fetchScopedUserSubcollection(user.id, "stressLogs"),
        fetchScopedUserSubcollection(user.id, "tasks"),
        fetchScopedUserSubcollection(user.id, "focusSessions"),
      ]);

      const averageStress =
        stressLogs.length > 0
          ? Number(
              (
                stressLogs.reduce(
                  (sum, entry) => sum + Number(entry.stressLevel || 0),
                  0
                ) / stressLogs.length
              ).toFixed(1)
            )
          : 0;

      const streak = Number(user.streak || 0);

      return {
        ...user,
        averageStress,
        stressLabel: getStressLevelLabel(averageStress),
        streak,
        focusSessionsCount: focusSessions.length,
        totalFocusMinutes: focusSessions.reduce(
          (sum, entry) => sum + Number(entry.minutes || 0),
          0
        ),
        tasksOpen: tasks.filter((entry) => entry.done !== true).length,
        tasksDone: tasks.filter((entry) => entry.done === true).length,
      };
    })
  );

  const classDetails = classes.map((klass) => {
    const inClass = userDetailsList.filter((user) =>
        sameClass(user.className, klass.name)
      );

    const elevated = inClass.filter((user) => Number(user.averageStress || 0) >= 4).length;
    const activeToday = inClass.filter((user) => user.lastActiveLabel === "Heute").length;
    const activeStreaks = inClass.filter((user) => Number(user.streak || 0) > 0).length;
    const bestStreak = Math.max(...inClass.map((user) => Number(user.streak || 0)), 0);

    let status = "Stabil";
    let statusTone = "ok";

    if (elevated >= 2) {
      status = "Erhöhte Belastung";
      statusTone = "warn";
    } else if (elevated === 1) {
      status = "Leicht auffällig";
      statusTone = "soft";
    }

    return {
      ...klass,
      ...buildTeacherMetaForClass(klass.name, teachers),
      total: inClass.length,
      elevated,
      activeToday,
      activeStreaks,
      bestStreak,
      status,
      statusTone,
      users: inClass,
    };
  });

  return {
    users: userDetailsList,
    classes: classDetails,
  };
}